import MaterialIcon from '@components/common/MaterialIcon';
import InputT from '@components/tailwind-components/Input/InputT';
import { MAX_DOCUMENT_FILE_SIZE, MAX_MEDIA_FILE_SIZE } from '@constants/env';
import type { TFile } from '@conversed/shared/src/central/types/common.type';
import { toastErrorTranslated } from '@helpers/toast';
import clsx from 'clsx';
import type React from 'react';
import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface FileUploadProps {
  onFileUpload: (file: TFile | string) => void;
  disableFromUrl?: boolean;
  accept?: string[];
  defaultValue?: string;
  type?: 'media' | 'file';
  align?: 'row' | 'col';
  error?: boolean;
  parentClassName?: string;
}

const FileUploadT: React.FC<FileUploadProps> = ({
  onFileUpload,
  disableFromUrl = true,
  accept,
  type = 'media',
  align = 'col',
  defaultValue,
  error,
  parentClassName = '',
}) => {
  const { control, watch, setValue } = useForm();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getImageContentDetails = useCallback(
    async (url: string): Promise<TFile> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }
          ctx.drawImage(img, 0, 0);
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('Failed to convert canvas to blob'));
              return;
            }
            const base64 = canvas.toDataURL('image/jpeg');
            const tFile: TFile = {
              name: 'image.jpg',
              type: 'image/jpeg',
              size: blob.size,
              base64,
            };
            resolve(tFile);
          }, 'image/jpeg');
        };
        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };
        img.src = url;
      });
    },
    [],
  );

  useEffect(() => {
    // If we do not have defaultValue we do not need to do this operation,so we are returning null for preview and file.
    if (!defaultValue) {
      setFile(null);
      setPreview(null);
      return;
    }

    // defaultValue is already a directly renderable URL/data URI, so it is used
    // as the preview as-is - no need to re-fetch and re-encode it through a
    // canvas just to populate this local preview state.
    setFile({
      name: '',
      type: type === 'file' ? 'file' : 'image/*',
      size: 1,
      base64: defaultValue,
    });
    setPreview(defaultValue);
  }, [type, defaultValue]);

  const [file, setFile] = useState<TFile | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];

      if (selectedFile?.type) {
        if (
          selectedFile.type.startsWith('image/') &&
          selectedFile.size > MAX_MEDIA_FILE_SIZE
        ) {
          toastErrorTranslated('general.validation.size', {
            size: MAX_MEDIA_FILE_SIZE / 1000000,
          });
          return;
        }

        if (
          [
            'application/vnd.ms-excel',
            'text/csv',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          ].includes(selectedFile.type) &&
          selectedFile.size > MAX_DOCUMENT_FILE_SIZE
        ) {
          toastErrorTranslated('general.validation.size', {
            size: MAX_DOCUMENT_FILE_SIZE / 1000000,
          });
          return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          const tFile: TFile = {
            name: selectedFile.name,
            type: selectedFile.type,
            size: selectedFile.size,
            base64,
          };
          if (selectedFile.type.startsWith('text/')) {
            setPreview(null);
          }
          if (selectedFile.type.startsWith('image/')) {
            setPreview(base64);
          }
          setFile(tFile);
          onFileUpload(tFile);
        };
        reader.readAsDataURL(selectedFile);
      }
    },
    [onFileUpload],
  );

  const imageUrl = watch('imageUrl', '');

  const handleCheckIconClick = useCallback(async () => {
    if (!imageUrl.length) {
      toastErrorTranslated('component.upload.by.imageUrl.error');
      return;
    }
    try {
      const imageData = await getImageContentDetails(imageUrl);
      setPreview(imageData.base64);
      setFile(imageData);
      onFileUpload(imageData);
    } catch (_error) {
      toastErrorTranslated('component.upload.by.imageUrl.not.valid');
    }
  }, [imageUrl, getImageContentDetails, onFileUpload]);

  const resetImageFields = () => {
    setFile(null);
    setPreview('');
    setValue('imageUrl', '');
    onFileUpload('');

    // Reset the file input value to allow selecting the same file again
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };
  const { t } = useTranslation();
  const renderContentByFileType = (file: TFile) => {
    if (file?.type.startsWith('image/')) {
      return (
        <div className='flex space-x-4 items-center w-full'>
          <div
            className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full bg-cover bg-center rounded-full '
            style={{ backgroundImage: `url(${preview})` }}
          />
        </div>
      );
    }

    return (
      <div className='flex space-x-4 items-center w-full min-w-[100px]'>
        <div className='absolute flex items-center justify-center top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full bg-cover bg-center rounded-full'>
          <MaterialIcon icon={'attach_file'} />
        </div>
      </div>
    );
  };

  return (
    <div className={`w-full my-1 ${parentClassName}`}>
      <div
        className={`${
          align === 'row' ? 'flex-row' : 'flex-col'
        } flex items-center f w-full relative`}>
        <div
          className={clsx(
            error
              ? 'border-red-base'
              : file
                ? 'border-success'
                : 'border-[#ccc]',
            type === 'file' ? 'rounded-md' : 'rounded-full',
            'border-2 min-w-[100px] h-[100px] relative flex items-center justify-center text-center cursor-pointer hover:bg-gray-200',
          )}>
          {file && (
            <div className='mb-2 absolute right-0 top-0 z-10 bg-white flex items-center w-fit rounded-full border'>
              <MaterialIcon
                icon='close'
                classes={['text-danger']}
                onClick={resetImageFields}
              />
            </div>
          )}

          <label className='w-full cursor-pointer'>
            <input
              type='file'
              className='hidden'
              accept={accept?.join(',')}
              onChange={handleFileChange}
              ref={fileInputRef}
            />

            {file ? (
              renderContentByFileType(file)
            ) : (
              <span className='text-pico flex items-center justify-center text-center p-2 w-full min-w-[100px]'>
                {t('component.upload.click')}
              </span>
            )}
          </label>
        </div>

        {disableFromUrl && (
          <div className='mt-3 flex items-center w-full'>
            <span className='mx-2 font-thin text-sm mb-2'>{t('or')}</span>
            <div className='flex items-center w-full'>
              <InputT
                parentClassName='w-full'
                field='imageUrl'
                type='text'
                placeholder={t('component.upload.get-image-url')}
                className='min-w-[100px]'
                control={control}
              />
              <div className='bg-primary-base flex items-center w-fit p-1 mb-2 ml-2 rounded-md'>
                <MaterialIcon
                  icon='check'
                  classes={['text-white']}
                  onClick={handleCheckIconClick}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploadT;
